import React, { useEffect, useRef, useState } from 'react';
import { Box, Text, Button, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';

import { inr, inrCompact, formatDay } from '../../utils/format';

const BAR = '#111111';      // single series: ink
const GRID = '#EDEDED';     // hairline gridlines, one step off the surface
const INK_MUTED = '#6B7280';
const HEIGHT = 180, PAD_L = 44, PAD_B = 22, PAD_T = 10;

// Clean y-axis ticks (0 / 2K / 4K …)
const niceMax = (max) => {
    if (max <= 0) return 1000;
    const step = Math.pow(10, Math.floor(Math.log10(max)));
    const n = max / step;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * step;
};

// Revenue per day/week as columns. Single series, so no legend box; the card title names it.
const RevenueChart = ({ series, bucket }) => {

    const wrapRef = useRef(null);
    const [width, setWidth] = useState(320);
    const [active, setActive] = useState(null);
    const [showTable, setShowTable] = useState(false);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, [showTable]);

    const max = niceMax(Math.max(...series.map(s => s.revenue), 0));
    const plotW = Math.max(width - PAD_L, 50), plotH = HEIGHT - PAD_B - PAD_T;
    const slot = plotW / series.length;
    const barW = Math.min(24, Math.max(3, slot - 2)); // cap thickness; keep a >=2px gap
    const y = (v) => PAD_T + plotH - (v / max) * plotH;
    const ticks = [0, max / 2, max];
    const labelEvery = Math.ceil(series.length / 4);
    const label = (s) => bucket === 'week' ? `Wk of ${formatDay(s.start)}` : formatDay(s.start);

    const activeItem = active !== null ? series[active] : null;

    return (
        <Box>
            <Box display='flex' justifyContent='flex-end' mb={1} >
                <Button size='xs' variant='ghost' fontWeight={500} textDecoration='underline' onClick={() => setShowTable(!showTable)} >{showTable ? 'Show chart' : 'Show table'}</Button>
            </Box>
            {showTable ?
                <Box maxHeight='240px' overflowY='auto' >
                    <Table size='sm' >
                        <Thead><Tr><Th>{bucket === 'week' ? 'Week of' : 'Day'}</Th><Th isNumeric>Orders</Th><Th isNumeric>Revenue</Th></Tr></Thead>
                        <Tbody>
                            {[...series].reverse().map(s => (
                                <Tr key={s.start}><Td>{formatDay(s.start)}</Td><Td isNumeric>{s.orders}</Td><Td isNumeric>{inr(s.revenue)}</Td></Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
                :
                <Box ref={wrapRef} position='relative' onMouseLeave={() => setActive(null)} >
                    <svg width={width} height={HEIGHT} role='img' aria-label={`Revenue per ${bucket}`} style={{ display: 'block', touchAction: 'pan-y' }} >
                        {ticks.map(t => (
                            <g key={t} >
                                <line x1={PAD_L} x2={width} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
                                <text x={PAD_L - 8} y={y(t)} dy='0.32em' textAnchor='end' fontSize={11} fill={INK_MUTED} >{inrCompact(t)}</text>
                            </g>
                        ))}
                        {series.map((s, i) => {
                            const x = PAD_L + i * slot + (slot - barW) / 2;
                            const top = y(s.revenue), h = PAD_T + plotH - top;
                            const r = Math.min(4, barW / 2, h);
                            return (
                                <g key={s.start} >
                                    {/* Hit target spans the whole slot, bigger than the mark */}
                                    <rect x={PAD_L + i * slot} y={PAD_T} width={slot} height={plotH} fill='transparent'
                                        onMouseEnter={() => setActive(i)} onClick={() => setActive(active === i ? null : i)} style={{ cursor: 'pointer' }} />
                                    {h > 0 &&
                                        <path
                                            d={`M${x},${top + h} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${top + h} Z`}
                                            fill={BAR}
                                            opacity={active === null || active === i ? 1 : 0.3}
                                            pointerEvents='none'
                                        />}
                                    {i % labelEvery === 0 &&
                                        <text x={PAD_L + i * slot + slot / 2} y={HEIGHT - 6} textAnchor='middle' fontSize={11} fill={INK_MUTED} >{formatDay(s.start)}</text>}
                                </g>
                            );
                        })}
                    </svg>
                    {activeItem &&
                        <Box position='absolute' top={0} left={`${Math.min(Math.max(PAD_L + active * slot + slot / 2 - 70, 0), width - 140)}px`} width='140px' bg='raano.black' color='raano.white' borderRadius='8px' px={3} py={2} pointerEvents='none' boxShadow='lg' >
                            <Text fontSize={12} opacity={0.8} >{label(activeItem)}</Text>
                            <Text fontSize={15} fontWeight={700} >{inr(activeItem.revenue)}</Text>
                            <Text fontSize={12} opacity={0.8} >{activeItem.orders} {activeItem.orders === 1 ? 'order' : 'orders'}</Text>
                        </Box>}
                </Box>
            }
        </Box>
    )
}

export default RevenueChart;
